import React, { useState, useMemo } from 'react';
import chunk from 'lodash/chunk';
import { Topic, VisitContext, UpdateVisitContextForm } from 'store/amwell/types';
import { MuiBox, MuiGrid, MuiDivider } from 'theme/material-ui';
import { ConnectCareCheckboxGroup, ConnectCareTextField } from 'components/ConnectCare';

export interface Props {
  columns: number;
  visitContext: VisitContext;
  loading: boolean;
  onVisitContextChange: (updateVisitContextForm: UpdateVisitContextForm) => void;
}

const getTopics = (visitContext: VisitContext) => {
  const { topics } = visitContext;

  return topics?.length ? topics : [];
};

// a chunck is an array of topics put into columns
const getChunks = (topics: Topic[], columns: number) => {
  return chunk(topics, Math.ceil(topics.length / columns));
};

function ConnectCareTriageTopics(props: Props) {
  const { columns, visitContext, onVisitContextChange, loading } = props;

  const [otherTopic, setOtherTopic] = useState(visitContext.otherTopic);
  const topics = useMemo(() => getTopics(visitContext), [visitContext]);
  const chunks = useMemo(() => getChunks(topics, columns), [topics, columns]);

  const onTopicChange = (chunkIndex: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const index = +e.target.id;
    const topicsToUpdate: Topic[] = [...visitContext.topics];

    const count = [...Array(chunkIndex + 1)].reduce((acc, _, idx, arr) => {
      return arr.length - 1 === idx
        ? acc + chunks[idx].slice(0, index).length
        : acc + chunks[idx].length;
    }, 0);

    topicsToUpdate[count].selected = e.target.checked;
    onVisitContextChange({ topics: topicsToUpdate });
  };

  if (!chunks.length) return null;

  const onOtherTopicBlur = () => {
    onVisitContextChange({ otherTopic });
  };

  return (
    <>
      <MuiGrid item xs>
        <MuiBox mb={2}>
          <MuiGrid container direction="row" justify="center" spacing={4} wrap="nowrap">
            {chunks.map((c, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <MuiGrid key={idx} item lg={4} md={6}>
                <ConnectCareCheckboxGroup
                  items={c}
                  disabled={loading}
                  itemToString={(item: any) => (item ? item.title : '')}
                  onChange={onTopicChange(idx)}
                  checkedKey="selected"
                />
              </MuiGrid>
            ))}
          </MuiGrid>
        </MuiBox>
        <MuiGrid container item>
          <MuiBox mb={1}>
            <ConnectCareTextField
              label="Other Condition"
              value={otherTopic}
              disabled={loading}
              onChange={e => setOtherTopic(e.target.value)}
              onBlur={onOtherTopicBlur}
            />
          </MuiBox>
        </MuiGrid>
      </MuiGrid>
      <MuiDivider variant="middle" />
    </>
  );
}

export default ConnectCareTriageTopics;
